import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Trash2, Loader2, Heart, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import AppIcon from "@/components/AppIcon";
import UrgencyBadge from "@/components/UrgencyBadge";

/**
 * Watchlist — /live-world/watchlist
 *
 * Server-persisted list of auctions the user is following. Shows image,
 * title, current bid, bid count, time remaining, status. Tap → auction
 * detail. Trash icon → unwatch (optimistic).
 */
function timeLeft(iso) {
  if (!iso) return "—";
  const ms = new Date(iso) - new Date();
  if (ms <= 0) return "ended";
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

export default function Watchlist() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get("/live-world/watchlist");
      setItems(r.data.items || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Please log in to see your watchlist.");
        nav("/");
        return;
      }
      toast.error("Couldn't load your watchlist.");
    } finally {
      setBusy(false);
    }
  }, [nav]);
  useEffect(() => { load(); }, [load]);

  // Ticker so time-remaining refreshes without a re-fetch.
  const [, forceTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => forceTick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);

  const unwatch = async (auction_id) => {
    // Optimistic
    setItems((s) => s.filter((it) => it.auction_id !== auction_id));
    try {
      await api.delete(`/live-world/auctions/${auction_id}/watch`);
      toast.success("Removed from watchlist");
    } catch {
      toast.error("Couldn't unwatch — reloading");
      load();
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-stone-50 pb-20" data-testid="lw-watchlist">
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-stone-200">
        <div className="max-w-2xl mx-auto px-3 py-2 flex items-center gap-2">
          <button onClick={() => nav("/live-world")} className="p-2 -ml-1 rounded-full hover:bg-stone-100" data-testid="lw-watchlist-back">
            <ArrowLeft size={18}/>
          </button>
          <Heart size={16} className="text-rose-500 fill-rose-500"/>
          <div className="font-black text-stone-900">My Watchlist</div>
          <div className="ml-auto text-[11px] text-stone-500 font-bold">
            {items.length} auction{items.length !== 1 ? "s" : ""}
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-3 space-y-2">
        {busy && items.length === 0 && (
          <div className="py-16 text-center text-stone-500">
            <Loader2 size={20} className="animate-spin inline-block mr-1.5"/> Loading…
          </div>
        )}
        {!busy && items.length === 0 && (
          <div className="py-16 text-center text-stone-500 text-sm" data-testid="lw-watchlist-empty">
            <div className="text-4xl mb-2">🔨</div>
            <div className="font-bold text-stone-700">No auctions on your watchlist yet</div>
            <div className="text-xs mt-1">Tap the ♡ on any auction to follow it here.</div>
            <button onClick={() => nav("/live-world")}
                    className="mt-4 px-4 py-2 rounded-full bg-[#E05E36] text-white font-black text-sm">
              Browse Live World
            </button>
          </div>
        )}
        {items.map((it) => (
          <div key={it.auction_id}
               className="bg-white border border-stone-200 rounded-2xl p-3 flex items-center gap-3"
               data-testid={`lw-watch-item-${it.auction_id}`}>
            <button onClick={() => it.status !== "gone" && nav(`/live-world/auction/${it.auction_id}`)}
                    className="w-16 h-16 rounded-xl bg-stone-50 grid place-items-center overflow-hidden shrink-0"
                    disabled={it.status === "gone"}>
              {it.image
                ? <img src={it.image} alt="" className="w-full h-full object-cover"/>
                : <AppIcon name={it.category || "auction"} size={52} fallback={<div className="text-2xl">🔨</div>}/>}
            </button>
            <button onClick={() => it.status !== "gone" && nav(`/live-world/auction/${it.auction_id}`)}
                    className="flex-1 min-w-0 text-left"
                    disabled={it.status === "gone"}>
              <div className="flex items-center gap-1.5 flex-wrap">
                {it.is_live
                  ? <UrgencyBadge urgency={it.urgency} closes_at={it.closes_at} compact/>
                  : (
                    <div className={`text-[9px] font-black px-1.5 py-0.5 rounded-full text-white ${
                      it.status === "gone" ? "bg-stone-400" : "bg-stone-500"
                    }`}>
                      {it.status === "gone" ? "GONE" : "🔒 CLOSED"}
                    </div>
                  )}
                {it.bid_position === "outbid" && (
                  <span className="inline-flex items-center gap-0.5 text-[9px] font-black px-1.5 py-0.5 rounded-full bg-rose-100 text-rose-700 border border-rose-200"
                        data-testid={`lw-outbid-${it.auction_id}`}>
                    <AlertCircle size={9}/> YOU'RE OUTBID
                  </span>
                )}
                {it.bid_position === "top" && (
                  <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
                    LEADING
                  </span>
                )}
              </div>
              <div className="font-black text-stone-900 text-sm truncate mt-0.5">{it.title}</div>
              <div className="flex items-center gap-3 text-xs mt-0.5">
                {it.current_bid != null && (
                  <div className="font-bold text-emerald-700">
                    ₹{Number(it.current_bid).toLocaleString("en-IN")}
                  </div>
                )}
                {it.bid_count != null && (
                  <div className="text-stone-500 font-bold">{it.bid_count} bids</div>
                )}
                {it.your_last_bid != null && (
                  <div className="text-stone-500 font-bold">
                    your: ₹{Number(it.your_last_bid).toLocaleString("en-IN")}
                  </div>
                )}
              </div>
            </button>
            <button onClick={() => unwatch(it.auction_id)}
                    className="p-2 rounded-full hover:bg-rose-50 text-rose-500"
                    title="Unwatch"
                    data-testid={`lw-unwatch-${it.auction_id}`}>
              <Trash2 size={16}/>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
